/**
 * Login Page
 * Main authentication page with elegant centered design
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/ui/components/ui/card';
import { LoginForm } from './components/LoginForm';
import { useAuth } from '@/features/auth/context/AuthContext';
import { AuthMode } from '@/dto';

export function LoginPage() {
  const [mode, setMode] = useState<AuthMode>(AuthMode.PASSWORD);
  const navigate = useNavigate();
  const { login, sendSignInLink, signInWithLink, isAuthenticated } = useAuth();

  // Get dynamic greeting based on time of day
  const getGreeting = (): string => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  // Handle email link (magic link) authentication on page load
  useEffect(() => {
    const handleEmailLink = async () => {
      const emailLink = window.location.href;

      // Check if this is a Clerk magic link sign-in
      if (emailLink.includes('__clerk_ticket')) {
        const email = window.localStorage.getItem('emailForSignIn');

        if (email) {
          try {
            await signInWithLink(email, emailLink);
          } catch (error) {
            console.error('Magic link sign-in failed:', error);
          }
        }
      }
    };

    handleEmailLink();
  }, [signInWithLink]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (email: string, password?: string): Promise<void> => {
    if (mode === AuthMode.EMAIL_LINK) {
      await sendSignInLink({
        email,
        continueUrl: window.location.origin + '/login',
        actionCodeSettings: {
          handleCodeInApp: true,
        },
      });
      alert('Sign-in link sent! Check your email.');
    } else if (mode === AuthMode.PASSWORD && password) {
      await login(email, password);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: 'radial-gradient(circle, #FFFFFF 0%, #F6EDE8 100%)'
      }}
    >
      <Card
        className="w-full max-w-[400px] relative"
        style={{
          backgroundColor: '#FFFFFF',
          border: '1px solid rgba(205, 188, 165, 0.3)',
          borderRadius: '12px',
          boxShadow: '0 10px 25px rgba(79, 56, 49, 0.1), 0 4px 10px rgba(79, 56, 49, 0.05)',
          padding: '40px 24px 24px'
        }}
      >
        {/* Organization Branding */}
        <div className="text-center mb-3">
          <h1
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: '28px',
              fontWeight: 500,
              letterSpacing: '0.5px',
              color: '#4F3831',
              marginBottom: '0'
            }}
          >
            Framewise
          </h1>
        </div>

        {/* Dynamic Greeting */}
        <h2
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: '24px',
            fontWeight: 400,
            color: '#4F3831',
            textAlign: 'center',
            lineHeight: 1.3,
            marginBottom: '32px'
          }}
        >
          {getGreeting()}
        </h2>

        {/* Decorative Divider */}
        <div
          style={{
            width: '60px',
            height: '3px',
            backgroundColor: '#8A4A1E',
            borderRadius: '1.5px',
            margin: '0 auto 24px'
          }}
        />

        <p
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: '14px',
            color: 'rgba(92, 64, 51, 0.8)',
            textAlign: 'center',
            marginBottom: '24px'
          }}
        >
          {mode === AuthMode.EMAIL_LINK
            ? 'Enter your email to receive a sign-in link'
            : 'Sign in to your Framewise account'
          }
        </p>

        <LoginForm mode={mode} onSubmit={handleSubmit} onModeChange={setMode} />

        {/* Footer */}
        <p
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: '12px',
            color: 'rgba(92, 64, 51, 0.6)',
            textAlign: 'center',
            marginTop: '24px'
          }}
        >
          By signing in, you agree to our Terms of Service and Privacy Policy
        </p>
      </Card>
    </div>
  );
}
